import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import type { AdminSession, AuditAction, AuditFilter, AuditOutcome } from "../admin-client";
import { auditExportUrl } from "../admin-client";
import { useAuditActions } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ShortID, toRFC3339, when } from "./common";

/**
 * The audit trail — every action an operator has taken in this realm,
 * including the ones that were refused.
 *
 * The denied rows are the interesting ones and the reason this table is
 * append-only in the database: an operator reaching for a permission they do
 * not hold, or pointing a customer credential at the Admin API, leaves a row
 * here whether or not anything happened. A viewer that showed only successes
 * would hide exactly the pattern it exists to reveal.
 *
 * Reading it needs `audit:read`. Taking a copy away needs `audit:export`,
 * which is a different grant — and the export writes a row of its own, so the
 * one act this trail could not otherwise record records itself.
 */
const PAGE = 200;

const OUTCOMES = ["allowed", "denied", "failed"];

export interface AuditSearch {
  outcome?: string;
  resource_type?: string;
  action?: string;
  since?: string;
  until?: string;
}

export function Audit({ search }: { search: AuditSearch }) {
  return <RequireOperator>{(session) => <AuditPage search={search} session={session} />}</RequireOperator>;
}

function AuditPage({ search, session }: { search: AuditSearch; session: AdminSession }) {
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState(search.outcome ?? "");
  const [resourceType, setResourceType] = useState(search.resource_type ?? "");
  const [action, setAction] = useState(search.action ?? "");
  const [since, setSince] = useState(search.since ?? "");
  const [until, setUntil] = useState(search.until ?? "");

  useEffect(() => {
    setOutcome(search.outcome ?? "");
    setResourceType(search.resource_type ?? "");
    setAction(search.action ?? "");
    setSince(search.since ?? "");
    setUntil(search.until ?? "");
  }, [search.outcome, search.resource_type, search.action, search.since, search.until]);

  // The filter the SERVER sees, built once and used for both the table and
  // the export link — so the file an operator downloads is the page they were
  // looking at, and not a different set that happens to share a heading.
  const filter: AuditFilter = {
    outcome: search.outcome,
    resource_type: search.resource_type,
    action: search.action,
    since: toRFC3339(search.since),
    until: toRFC3339(search.until),
    limit: PAGE,
  };
  const actions = useAuditActions(filter);
  const mayExport = can(session.operator, "audit:export");

  return (
    <>
      <PageHeader
        title="Audit"
        description="Every admin action, its reason code and its outcome — refusals included."
      />

      <Card
        title="Filter"
        actions={
          mayExport ? (
            // An anchor, not a fetch. The browser carries the HttpOnly admin
            // cookie on a same-origin navigation, takes the filename from the
            // server's Content-Disposition and streams the file to disk —
            // none of which this tab would do better by pulling the whole
            // trail into memory as a blob first.
            <a className="esm-button esm-button--secondary" href={auditExportUrl(filter)} download>
              Export CSV
            </a>
          ) : null
        }
      >
        <form
          className="esm-filters"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              to: "/audit",
              search: {
                outcome: outcome || undefined,
                resource_type: resourceType.trim() || undefined,
                action: action.trim() || undefined,
                since: since || undefined,
                until: until || undefined,
              },
            });
          }}
        >
          <Field label="Outcome" htmlFor="audit-outcome">
            <Select
              id="audit-outcome"
              value={outcome}
              onChange={(event) => setOutcome(event.target.value)}
            >
              <option value="">Any outcome</option>
              {OUTCOMES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Resource type"
            htmlFor="audit-resource"
            hint="Exactly as recorded — user, device, wallet, permission."
          >
            <Input
              id="audit-resource"
              value={resourceType}
              onChange={(event) => setResourceType(event.target.value)}
            />
          </Field>
          <Field
            label="Action"
            htmlFor="audit-action"
            hint="The full dotted name, e.g. admin.user.suspend."
          >
            <Input
              id="audit-action"
              value={action}
              onChange={(event) => setAction(event.target.value)}
            />
          </Field>
          {/* datetime-local, converted to a UTC instant before it is sent.
              The server refuses anything that is not RFC 3339 rather than
              guessing a timezone — an audit range that silently shifted by
              the server's offset is an export that misses the hour asked
              about. */}
          <Field label="From" htmlFor="audit-since">
            <Input
              id="audit-since"
              type="datetime-local"
              value={since}
              onChange={(event) => setSince(event.target.value)}
            />
          </Field>
          <Field label="To" htmlFor="audit-until">
            <Input
              id="audit-until"
              type="datetime-local"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
            />
          </Field>
          <Button type="submit" busy={actions.isFetching}>
            Apply
          </Button>
        </form>
        {mayExport ? null : (
          <Alert tone="info" title="Export needs its own permission">
            Reading this page needs <code>audit:read</code>. Downloading it needs{" "}
            <code>audit:export</code>, which your roles do not grant — taking a copy of the audit
            trail off the platform is a different trust from reading it here.
          </Alert>
        )}
      </Card>

      <ErrorAlert error={actions.error} title="Could not load the audit trail" />

      <Card title={`Actions — the last ${PAGE}, newest first`}>
        {actions.isPending ? (
          <Spinner label="Loading the audit trail" />
        ) : (
          <DataTable<AuditAction>
            rows={actions.data?.actions ?? []}
            rowKey={(row) => row.id}
            empty="No action matches that filter."
            columns={[
              { key: "occurred", header: "When", render: (row) => when(row.occurred_at) },
              {
                key: "operator",
                header: "Operator",
                // "unknown" is what the server records for an action nobody
                // was signed in for — a cross-realm rejection, a failed
                // sign-in. Shown as recorded rather than blanked, because the
                // row is evidence and evidence is not tidied up.
                render: (row) => (
                  <>
                    {row.admin_email}
                    <div className="esm-muted-text">
                      <ShortID value={row.admin_user_id} />
                    </div>
                  </>
                ),
              },
              {
                key: "outcome",
                header: "Outcome",
                render: (row) => <Badge tone={outcomeTone(row.outcome)}>{row.outcome}</Badge>,
              },
              { key: "action", header: "Action", render: (row) => <code>{row.action}</code> },
              {
                key: "resource",
                header: "Resource",
                render: (row) => (
                  <>
                    <code>{row.resource_type}</code>
                    <div className="esm-muted-text">
                      <ShortID value={row.resource_id} />
                    </div>
                  </>
                ),
              },
              {
                key: "reason",
                header: "Reason",
                // none_supplied is the SYSTEM's code — a read, a refusal, a
                // sign-in. It is rendered differently from an operator's
                // chosen reason so the two are never confused: a mutation
                // cannot reach a handler without a real one.
                render: (row) => (
                  <>
                    {row.reason_code === "none_supplied" ? (
                      <span className="esm-muted-text">not required</span>
                    ) : (
                      <code>{row.reason_code}</code>
                    )}
                    {row.reason_note ? <div className="esm-muted-text">{row.reason_note}</div> : null}
                  </>
                ),
              },
              {
                key: "detail",
                header: "Detail",
                render: (row) =>
                  row.detail && Object.keys(row.detail).length > 0 ? (
                    <code className="esm-short-id">{JSON.stringify(row.detail)}</code>
                  ) : (
                    <span className="esm-muted-text">—</span>
                  ),
              },
              {
                key: "ip",
                header: "From",
                render: (row) => (
                  <>
                    <code>{row.ip || "—"}</code>
                    <div className="esm-muted-text">
                      <ShortID value={row.request_id} />
                    </div>
                  </>
                ),
              },
            ]}
          />
        )}
      </Card>
    </>
  );
}

function outcomeTone(outcome: AuditOutcome): BadgeTone {
  switch (outcome) {
    case "allowed":
      return "success";
    case "denied":
      return "danger";
    default:
      return "warning";
  }
}
